"use client";

import { useEffect } from "react";
import { useUser } from "@clerk/nextjs";

type RefgrowFn = (amount: number, event: "signup" | "purchase", email: string) => void;

/**
 * Bridges Clerk signups to Refgrow's email-fallback attribution.
 * Fires Refgrow(0, "signup", email) once per Clerk user per device
 * (localStorage-guarded) so Refgrow records the email<->referral-cookie
 * association. Without this, the priority-4 email fallback on Stripe webhook
 * has no data to match against.
 *
 * The Refgrow script loads async via <Script strategy="afterInteractive" /> in
 * root layout, so window.Refgrow may not be defined yet when this effect
 * first fires. We poll briefly (up to ~10s) and give up — Refgrow's webhook
 * still has other attribution paths (cookie → checkout metadata) as a safety.
 */
export function RefgrowIdentify() {
  const { user, isLoaded } = useUser();

  useEffect(() => {
    if (!isLoaded || !user) return;
    const email = user.primaryEmailAddress?.emailAddress;
    if (!email) return;

    const key = "refgrow_signup_reported";
    if (localStorage.getItem(key) === user.id) return;

    const tryFire = (): boolean => {
      const refgrow = (window as unknown as { Refgrow?: RefgrowFn }).Refgrow;
      if (typeof refgrow !== "function") return false;
      refgrow(0, "signup", email);
      localStorage.setItem(key, user.id);
      return true;
    };

    if (tryFire()) return;

    let attempts = 0;
    const maxAttempts = 50; // 50 * 200ms = 10s
    const interval = window.setInterval(() => {
      attempts += 1;
      if (tryFire() || attempts >= maxAttempts) {
        window.clearInterval(interval);
      }
    }, 200);
    return () => window.clearInterval(interval);
  }, [isLoaded, user]);

  return null;
}
